import { Button, notification, Tabs } from 'antd';
import { useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import { TItemsTab } from 'shared/types/types';
import { RolesUser } from 'shared/enums';
import { configTabs, createDayOffExcelCommonInfo, createPollExcelCommonInfo } from 'shared/helpers/utils';
import { useTabsNavigate } from 'shared/hooks';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import { WorkSheet } from 'xlsx-js-style';
import * as XLSX from 'xlsx-js-style';
import { useApproveDayoffMutation, useLazyDayoffsQuery, useRejectDayoffMutation } from 'app/services/edpApiV1/dayoff';
import { DayOffsStatusesMap, DayOffTabs } from 'shared/types/dayoff.types';
import moment from 'moment';
import { PICKER_DATE_MASK } from 'shared/constants';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { useState } from 'react';
import { VacationModal } from 'features/vacations/vacationModal';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { DayOffPull } from './dayOffPull';
import { DayOffInProgress } from './dayOffInProgress';
import { DayOffArchive } from './dayOffArchive';
import style from './index.module.scss';

const breadCrumbs = [{ name: 'Отгулы', link: '/dayoff' }];

const itemsTab: TItemsTab = [
  {
    label: 'Общий список заявок',
    key: 'new',
    children: <DayOffPull />,
    role: [RolesUser.HRDirector, RolesUser.FirstLine, RolesUser.SecondLine],
  },
  {
    label: 'В работе',
    key: 'my',
    children: <DayOffInProgress />,
    role: [RolesUser.HRDirector, RolesUser.FirstLine, RolesUser.SecondLine],
  },
  {
    label: 'Архив',
    key: 'myArchive',
    children: <DayOffArchive />,
    role: [RolesUser.HRDirector, RolesUser.FirstLine, RolesUser.SecondLine],
  },
];

export const DayOff = () => {
  const [activeTab, handler] = useTabsNavigate('dayoff', itemsTab[0].key);
  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);
  const tabPane = configTabs(role, itemsTab);
  const dayOffSelected = useAppSelector(state => state.entities.dayOff.dayOffSelected);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const [reject, { isLoading: isRejectLoading }] = useRejectDayoffMutation();
  const [approve, { isLoading: isApproveLoading }] = useApproveDayoffMutation();

  const [getDayOffs] = useLazyDayoffsQuery();

  const [openModal, setOpenModal] = useState<boolean>(false);

  const download = async () => {
    const workbook = XLSX.utils.book_new();
    const commonData = createDayOffExcelCommonInfo();

    const dataSourcecMock = await getDayOffs({
      type: activeTab as DayOffTabs,
      all: true,
      allAvailableOrganizations,
    }).unwrap();

    for (const dayOff of dataSourcecMock?.data ?? []) {
      const fio = `${dayOff.employee.lastName} ${dayOff.employee.name} ${dayOff.employee.middleName}`;
      commonData.push([
        String(dayOff.id),
        moment(dayOff.created).format(PICKER_DATE_MASK),
        fio,
        moment(dayOff.date).format(PICKER_DATE_MASK),
        String(dayOff.amount),
        DayOffsStatusesMap[dayOff.status],
      ]);
    }
    const newWs: WorkSheet = XLSX.utils.aoa_to_sheet(commonData);
    XLSX.utils.book_append_sheet(workbook, newWs);
    XLSX.writeFile(workbook, `${'dayoff'}.xlsx`, { bookType: 'xlsx', bookSST: false });
  };

  const approveDayOffs = async () => {
    const approvedArray: string[] = [];
    for (const dayOff of dayOffSelected) {
      try {
        const approved = await approve(dayOff.id).unwrap();
        approvedArray.push(approved);
      } catch (e: any) {
        notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
      }
    }
    if (approvedArray.length) {
      notification.success({ message: 'Отгулы согласованы' });
    }
  };

  const openRejectionModal = () => {
    setOpenModal(true);
  };

  const rejectDayOffs = async (rejectionReason: string) => {
    const approvedArray: string[] = [];
    for (const dayOff of dayOffSelected) {
      try {
        const approved = await reject({ requestId: dayOff.id, rejectionReason }).unwrap();
        approvedArray.push(approved);
      } catch (e: any) {
        notification.error({ message: returnErrorOrMessage(e, 'Что-то пошло не так') });
      }
    }
    if (approvedArray.length) {
      notification.success({ message: 'Отгулы отклонены' });
    }
  };

  return (
    <>
      <div className={style.vacations__panel}>
        <h3>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <div className={style.buttons}>
          {activeTab === 'my' ? (
            <>
              <VacationModal
                id="1"
                openModal={openModal}
                setOpenModal={setOpenModal}
                handleSubmit={rejectDayOffs}
                loading={isRejectLoading}
              />
              <Button
                type="default"
                disabled={!dayOffSelected.length || isApproveLoading}
                loading={isRejectLoading}
                onClick={openRejectionModal}
              >
                Отклонить
              </Button>
              <Button
                type="primary"
                disabled={!dayOffSelected.length || isRejectLoading}
                onClick={approveDayOffs}
                loading={isApproveLoading}
              >
                Согласовать
              </Button>
            </>
          ) : null}

          <Button type="default" onClick={download}>
            Скачать
          </Button>
        </div>
      </div>

      <div className={style.wrap}>
        {role && (
          <Tabs activeKey={activeTab} onChange={handler} destroyInactiveTabPane>
            {tabPane &&
              tabPane.map(el => {
                return (
                  <Tabs.TabPane tab={el.label} key={el.key}>
                    {el.children}
                  </Tabs.TabPane>
                );
              })}
          </Tabs>
        )}
      </div>
    </>
  );
};
